import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  ArrowUpTrayIcon,
  MapIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  DocumentArrowUpIcon,
  AdjustmentsHorizontalIcon,
  MagnifyingGlassIcon,
  CloudArrowUpIcon
} from '@heroicons/react/24/outline';

const STEPS = [
  { id: 1, name: 'Upload File', icon: DocumentArrowUpIcon },
  { id: 2, name: 'Select Features', icon: MapIcon },
  { id: 3, name: 'Map Columns', icon: AdjustmentsHorizontalIcon },
  { id: 4, name: 'Match & Preview', icon: MagnifyingGlassIcon },
  { id: 5, name: 'Import', icon: CloudArrowUpIcon }
];

const GeospatialImport = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  // File and parsing state
  const [uploadedFile, setUploadedFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);

  // Feature selection state
  const [selectedFeatures, setSelectedFeatures] = useState(new Set());
  const [featureFilter, setFeatureFilter] = useState('');

  // Flex field definitions for plots
  const [flexFieldDefs, setFlexFieldDefs] = useState([]);
  const [flexFieldMapping, setFlexFieldMapping] = useState({});

  // Column mapping state
  const [columnMapping, setColumnMapping] = useState({
    alias: '',
    section: ''
  });

  // Import mode state
  const [importMode, setImportMode] = useState('upsert'); // 'create', 'update', 'upsert'
  const [matchField, setMatchField] = useState('alias');

  // Coordinate system state
  const [sourceSRID, setSourceSRID] = useState(4326);

  // Match preview state
  const [matchPreview, setMatchPreview] = useState(null);

  // Import result state
  const [importResult, setImportResult] = useState(null);

  // Fetch flex field definitions for plots on mount
  useEffect(() => {
    const fetchFlexFields = async () => {
      try {
        const response = await fetch('/api/flex-fields/definitions?entityType=plot', {
          credentials: 'include'
        });
        if (response.ok) {
          setFlexFieldDefs(await response.json());
        }
      } catch (err) {
        console.error('Error fetching flex fields:', err);
      }
    };
    fetchFlexFields();
  }, []);

  // Get filtered features
  const getFilteredFeatures = useCallback(() => {
    if (!parsedData?.features) return [];
    if (!featureFilter) return parsedData.features;

    const lowerFilter = featureFilter.toLowerCase();
    return parsedData.features.filter(f => {
      const props = f.properties || {};
      return Object.values(props).some(v =>
        String(v).toLowerCase().includes(lowerFilter)
      );
    });
  }, [parsedData, featureFilter]);

  // Handle file upload
  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsLoading(true);
    setError(null);
    setUploadedFile(file);
    setParsedData(null);
    setSelectedFeatures(new Set());

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/geospatial/parse', {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to parse file');
      }

      setParsedData(result);
      // Set detected SRID from parsed data
      setSourceSRID(result.detectedSRID || 4326);

      // Select all polygon features by default
      const polygonIndices = new Set(
        result.features
          .filter(f => f.geometry && (f.geometry.type === 'Polygon' || f.geometry.type === 'MultiPolygon' || f.geometry.type === 'WKT'))
          .map(f => f._index)
      );
      setSelectedFeatures(polygonIndices);

      // Auto-detect column mapping
      const attrs = result.attributes.map(a => a.toLowerCase());
      if (attrs.includes('alias') || attrs.includes('name') || attrs.includes('plot_name') || attrs.includes('plotname')) {
        const aliasAttr = result.attributes.find(a =>
          ['alias', 'name', 'plot_name', 'plotname'].includes(a.toLowerCase())
        );
        setColumnMapping(prev => ({ ...prev, alias: aliasAttr || '' }));
      }
      if (attrs.includes('section') || attrs.includes('block') || attrs.includes('area')) {
        const sectionAttr = result.attributes.find(a =>
          ['section', 'block', 'area'].includes(a.toLowerCase())
        );
        setColumnMapping(prev => ({ ...prev, section: sectionAttr || '' }));
      }

      setCurrentStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle select all/none
  const handleSelectAll = () => {
    const filtered = getFilteredFeatures();
    const polygonIndices = new Set(
      filtered
        .filter(f => f.geometry && (f.geometry.type === 'Polygon' || f.geometry.type === 'MultiPolygon' || f.geometry.type === 'WKT'))
        .map(f => f._index)
    );
    setSelectedFeatures(polygonIndices);
  };

  const handleSelectNone = () => {
    setSelectedFeatures(new Set());
  };

  const toggleFeature = (index) => {
    setSelectedFeatures(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Fetch match preview
  const fetchMatchPreview = async () => {
    if (importMode === 'create') {
      setMatchPreview(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const selectedFeaturesList = parsedData.features.filter(f => selectedFeatures.has(f._index));

      const response = await fetch('/api/geospatial/preview-match', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          features: selectedFeaturesList,
          columnMapping,
          matchField
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to preview matches');
      }

      setMatchPreview(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle import
  const handleImport = async () => {
    setIsLoading(true);
    setError(null);
    setImportResult(null);

    try {
      // Update features with user-selected SRID
      const selectedFeaturesList = parsedData.features
        .filter(f => selectedFeatures.has(f._index))
        .map(f => ({
          ...f,
          _sourceSRID: sourceSRID
        }));

      const response = await fetch('/api/geospatial/import', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          features: selectedFeaturesList,
          columnMapping,
          matchMode: importMode,
          matchField,
          flexFieldMapping
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Import failed');
      }

      setImportResult(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Check if coordinates look like they're in a projected system
  const coordinatesLookProjected = useCallback(() => {
    const sampleFeature = parsedData?.features?.find(f => f.geometry?.coordinates);
    if (sampleFeature) {
      let coords = sampleFeature.geometry.coordinates;
      while (Array.isArray(coords[0])) coords = coords[0];
      const [x, y] = coords;
      return Math.abs(x) > 180 || Math.abs(y) > 90;
    }
    return false;
  }, [parsedData]);

  // Step navigation
  const canGoNext = () => {
    switch (currentStep) {
      case 1:
        return parsedData && parsedData.features.length > 0;
      case 2:
        return selectedFeatures.size > 0;
      case 3:
        // If coordinates look projected but user selected WGS84 (4326), warn them
        // SRID -1 means custom but not entered yet
        if (sourceSRID === -1) return false;
        return true;
      case 4:
        return true;
      default:
        return false;
    }
  };

  const goNext = async () => {
    if (currentStep === 3) {
      await fetchMatchPreview();
    }
    if (currentStep === 4) {
      await handleImport();
    }
    setCurrentStep(prev => Math.min(prev + 1, 5));
  };

  const goBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const resetWizard = () => {
    setCurrentStep(1);
    setUploadedFile(null);
    setParsedData(null);
    setSelectedFeatures(new Set());
    setColumnMapping({ alias: '', section: '' });
    setFlexFieldMapping({});
    setImportMode('upsert');
    setMatchField('alias');
    setSourceSRID(4326);
    setMatchPreview(null);
    setImportResult(null);
    setError(null);
    setFeatureFilter('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Render step indicator
  const renderStepIndicator = () => (
    <nav aria-label="Progress" className="mb-8">
      <ol className="flex items-center">
        {STEPS.map((step, stepIdx) => (
          <li key={step.id} className={`relative ${stepIdx !== STEPS.length - 1 ? 'pr-8 sm:pr-20 flex-1' : ''}`}>
            {step.id < currentStep ? (
              <>
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className="h-0.5 w-full bg-brand-green-600" />
                </div>
                <div className="relative flex h-8 w-8 items-center justify-center rounded-full bg-brand-green-600">
                  <CheckCircleIcon className="h-5 w-5 text-white" aria-hidden="true" />
                </div>
              </>
            ) : step.id === currentStep ? (
              <>
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className="h-0.5 w-full bg-gray-200" />
                </div>
                <div className="relative flex h-8 w-8 items-center justify-center rounded-full border-2 border-brand-green-600 bg-white">
                  <step.icon className="h-4 w-4 text-brand-green-600" aria-hidden="true" />
                </div>
              </>
            ) : (
              <>
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className="h-0.5 w-full bg-gray-200" />
                </div>
                <div className="relative flex h-8 w-8 items-center justify-center rounded-full border-2 border-gray-300 bg-white">
                  <step.icon className="h-4 w-4 text-gray-500" aria-hidden="true" />
                </div>
              </>
            )}
            <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs text-gray-500 whitespace-nowrap hidden sm:block">
              {step.name}
            </span>
          </li>
        ))}
      </ol>
    </nav>
  );

  // Step 1: Upload File
  const renderUploadStep = () => (
    <div className="text-center py-8">
      <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-brand-green-100 mb-6">
        <ArrowUpTrayIcon className="h-10 w-10 text-brand-green-600" />
      </div>

      <h2 className="text-xl font-semibold mb-2">Upload Geospatial File</h2>
      <p className="text-gray-600 mb-6 max-w-md mx-auto">
        Upload a file containing plot geometry data. Supported formats include Shapefile (ZIP),
        KML, KMZ, GeoJSON, and CSV with geometry columns.
      </p>

      <input
        ref={fileInputRef}
        type="file"
        accept=".zip,.kmz,.kml,.geojson,.json,.csv,.shp"
        onChange={handleFileChange}
        className="hidden"
      />

      <button
        onClick={() => fileInputRef.current?.click()}
        disabled={isLoading}
        className="inline-flex items-center gap-2 px-6 py-3 bg-brand-green-600 text-white rounded-lg hover:bg-brand-green-700 disabled:bg-brand-green-300 transition-colors"
      >
        {isLoading ? (
          <>
            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Parsing File...
          </>
        ) : (
          <>
            <ArrowUpTrayIcon className="h-5 w-5" />
            Select File
          </>
        )}
      </button>

      {uploadedFile && !parsedData && (
        <p className="mt-4 text-sm text-gray-500">
          Selected: {uploadedFile.name}
        </p>
      )}

      <div className="mt-8 bg-gray-50 rounded-lg p-4 text-left max-w-lg mx-auto">
        <h3 className="font-medium text-gray-900 mb-2">Supported File Formats</h3>
        <ul className="text-sm text-gray-600 space-y-1">
          <li><strong>Shapefile (.zip)</strong> - ESRI Shapefile compressed in a ZIP archive</li>
          <li><strong>KMZ (.kmz)</strong> - Compressed KML file from Google Earth</li>
          <li><strong>KML (.kml)</strong> - Keyhole Markup Language from Google Earth</li>
          <li><strong>GeoJSON (.geojson, .json)</strong> - Standard GeoJSON format</li>
          <li><strong>CSV (.csv)</strong> - CSV with a geometry column (GeoJSON or WKT format)</li>
        </ul>
      </div>
    </div>
  );

  // Step 2: Select Features
  const renderSelectStep = () => {
    const filteredFeatures = getFilteredFeatures();
    const polygonCount = filteredFeatures.filter(f =>
      f.geometry && (f.geometry.type === 'Polygon' || f.geometry.type === 'MultiPolygon' || f.geometry.type === 'WKT')
    ).length;

    return (
      <div>
        <h2 className="text-xl font-semibold mb-2">Select Features to Import</h2>
        <p className="text-gray-600 mb-4">
          Review the features found in your file and select which ones to import as plots.
        </p>

        <div className="bg-blue-50 rounded-lg p-4 mb-4">
          <p className="text-sm text-blue-800">
            Found <strong>{parsedData.totalFeatures}</strong> total features,
            <strong> {parsedData.polygonFeatures}</strong> are polygons suitable for import.
            <strong> {selectedFeatures.size}</strong> selected.
          </p>
        </div>

        {/* Search and actions */}
        <div className="flex flex-wrap gap-3 mb-4">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Filter features by attribute value..."
              value={featureFilter}
              onChange={(e) => setFeatureFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-brand-green-500 focus:border-brand-green-500"
            />
          </div>
          <button
            onClick={handleSelectAll}
            className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md"
          >
            Select All Polygons
          </button>
          <button
            onClick={handleSelectNone}
            className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md"
          >
            Select None
          </button>
        </div>

        {/* Feature list */}
        <div className="border rounded-lg overflow-hidden max-h-96 overflow-y-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Select
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  #
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Geometry
                </th>
                {parsedData.attributes.slice(0, 4).map(attr => (
                  <th key={attr} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {attr}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredFeatures.map((feature, idx) => {
                const isPolygon = feature.geometry &&
                  (feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon' || feature.geometry.type === 'WKT');
                const isSelected = selectedFeatures.has(feature._index);

                return (
                  <tr
                    key={feature._index}
                    className={`${isSelected ? 'bg-brand-green-50' : ''} ${!isPolygon ? 'opacity-50' : 'cursor-pointer hover:bg-gray-50'}`}
                    onClick={() => isPolygon && toggleFeature(feature._index)}
                  >
                    <td className="px-4 py-2">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={!isPolygon}
                        onChange={() => toggleFeature(feature._index)}
                        className="h-4 w-4 text-brand-green-600 focus:ring-brand-green-500 border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-500">
                      {feature._index + 1}
                    </td>
                    <td className="px-4 py-2 text-sm">
                      <span className={`inline-flex px-2 py-1 text-xs rounded ${
                        isPolygon
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {feature.geometry?.type || 'No geometry'}
                      </span>
                    </td>
                    {parsedData.attributes.slice(0, 4).map(attr => (
                      <td key={attr} className="px-4 py-2 text-sm text-gray-900 max-w-[150px] truncate">
                        {feature.properties?.[attr] ?? '-'}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredFeatures.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No features match your filter
          </div>
        )}
      </div>
    );
  };

  // Step 3: Map Columns
  const renderMapColumnsStep = () => (
    <div>
      <h2 className="text-xl font-semibold mb-2">Map Columns</h2>
      <p className="text-gray-600 mb-6">
        Map attributes from your file to plot fields. This allows you to automatically set plot names,
        sections, and other properties during import.
      </p>

      <div className="grid gap-6 md:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Alias (Plot Name/ID)
            <span className="text-gray-400 font-normal ml-2">Recommended</span>
          </label>
          <select
            value={columnMapping.alias}
            onChange={(e) => setColumnMapping(prev => ({ ...prev, alias: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-brand-green-500 focus:border-brand-green-500"
          >
            <option value="">-- None --</option>
            {parsedData?.attributes.map(attr => (
              <option key={attr} value={attr}>{attr}</option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            The unique identifier for each plot (e.g., "A-001", "Block 2 Row 3")
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Section
            <span className="text-gray-400 font-normal ml-2">Optional</span>
          </label>
          <select
            value={columnMapping.section}
            onChange={(e) => setColumnMapping(prev => ({ ...prev, section: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-brand-green-500 focus:border-brand-green-500"
          >
            <option value="">-- None --</option>
            {parsedData?.attributes.map(attr => (
              <option key={attr} value={attr}>{attr}</option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            The section or area of the cemetery (e.g., "Section A", "Memorial Garden")
          </p>
        </div>
      </div>

      {/* Flex Field Mapping */}
      {flexFieldDefs.length > 0 && (
        <div className="mt-6">
          <h3 className="font-medium text-gray-900 mb-3">Custom Field Mapping</h3>
          <p className="text-sm text-gray-600 mb-4">
            Map attributes from your file to custom plot fields. Values will be imported automatically.
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            {flexFieldDefs.map(def => (
              <div key={def.id}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {def.fieldLabel}
                  <span className="text-gray-400 font-normal ml-2">Custom Field</span>
                </label>
                <select
                  value={flexFieldMapping[def.id] || ''}
                  onChange={(e) => setFlexFieldMapping(prev => ({ ...prev, [def.id]: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-brand-green-500 focus:border-brand-green-500"
                >
                  <option value="">-- None --</option>
                  {parsedData?.attributes.map(attr => (
                    <option key={attr} value={attr}>{attr}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Coordinate System */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="font-medium text-gray-900 mb-3">Coordinate System (SRID)</h3>

        {/* Sample coordinates preview */}
        {(() => {
          const sampleFeature = parsedData?.features?.find(f => f.geometry?.coordinates);
          if (sampleFeature) {
            let coords = sampleFeature.geometry.coordinates;
            while (Array.isArray(coords[0])) coords = coords[0];
            const [x, y] = coords;
            const looksProjected = Math.abs(x) > 180 || Math.abs(y) > 90;

            return (
              <div className={`mb-4 p-3 rounded-md ${looksProjected ? 'bg-yellow-100 border border-yellow-300' : 'bg-green-100 border border-green-300'}`}>
                <p className="text-sm font-medium mb-1">
                  {looksProjected ? 'Warning: Coordinates appear to be in a projected system' : 'Coordinates appear to be in GPS format'}
                </p>
                <p className="text-xs text-gray-700">
                  Sample coordinate: X = <strong>{x?.toFixed(2)}</strong>, Y = <strong>{y?.toFixed(2)}</strong>
                </p>
                {looksProjected && (
                  <p className="text-xs text-yellow-800 mt-1">
                    These look like projected coordinates (feet/meters). Please select the correct coordinate system below or coordinates will not display correctly on the map.
                  </p>
                )}
              </div>
            );
          }
          return null;
        })()}

        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <select
              value={sourceSRID}
              onChange={(e) => setSourceSRID(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-brand-green-500 focus:border-brand-green-500"
            >
              <option value={4326}>EPSG:4326 - WGS 84 (GPS coordinates)</option>
              <option value={0}>Unknown / Auto-detect</option>
              <optgroup label="Texas State Plane (ft)">
                <option value={2276}>EPSG:2276 - NAD83 / Texas North (ft)</option>
                <option value={2277}>EPSG:2277 - NAD83 / Texas North Central (ft)</option>
                <option value={2278}>EPSG:2278 - NAD83 / Texas Central (ft)</option>
                <option value={2279}>EPSG:2279 - NAD83 / Texas South Central (ft)</option>
                <option value={2280}>EPSG:2280 - NAD83 / Texas South (ft)</option>
              </optgroup>
              <optgroup label="Texas State Plane (meters)">
                <option value={32137}>EPSG:32137 - NAD83 / Texas North</option>
                <option value={32138}>EPSG:32138 - NAD83 / Texas North Central</option>
                <option value={32139}>EPSG:32139 - NAD83 / Texas Central</option>
                <option value={32140}>EPSG:32140 - NAD83 / Texas South Central</option>
                <option value={32141}>EPSG:32141 - NAD83 / Texas South</option>
              </optgroup>
              <optgroup label="UTM Zones (NAD83)">
                <option value={26913}>EPSG:26913 - NAD83 / UTM zone 13N</option>
                <option value={26914}>EPSG:26914 - NAD83 / UTM zone 14N</option>
                <option value={26915}>EPSG:26915 - NAD83 / UTM zone 15N</option>
                <option value={26916}>EPSG:26916 - NAD83 / UTM zone 16N</option>
                <option value={26917}>EPSG:26917 - NAD83 / UTM zone 17N</option>
                <option value={26918}>EPSG:26918 - NAD83 / UTM zone 18N</option>
                <option value={26919}>EPSG:26919 - NAD83 / UTM zone 19N</option>
              </optgroup>
              <optgroup label="Other">
                <option value={3857}>EPSG:3857 - Web Mercator</option>
                <option value={-1}>Custom EPSG code...</option>
              </optgroup>
            </select>
          </div>
          {sourceSRID === -1 && (
            <div className="flex-1 min-w-[150px]">
              <input
                type="number"
                placeholder="Enter EPSG code"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-brand-green-500 focus:border-brand-green-500"
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  if (val > 0) setSourceSRID(val);
                }}
              />
            </div>
          )}
          <div className="text-sm text-gray-600">
            {sourceSRID === 4326 ? (
              <span className="text-green-600">Coordinates are in latitude/longitude</span>
            ) : sourceSRID === 0 ? (
              <span className="text-yellow-600">Unknown projection - coordinates may not display correctly</span>
            ) : sourceSRID === -1 ? (
              <span className="text-blue-600">Enter custom EPSG code</span>
            ) : (
              <span className="text-blue-600">Coordinates will be transformed to WGS84</span>
            )}
          </div>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          Select the coordinate system used in your file. Look at the .prj file or check with your GIS source.
          Texas typically uses State Plane coordinates (EPSG:2276-2280 for feet).
        </p>
      </div>

      {/* Preview */}
      {columnMapping.alias && (
        <div className="mt-8">
          <h3 className="font-medium text-gray-900 mb-3">Preview (first 5 records)</h3>
          <div className="border rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Alias</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Section</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {parsedData.features
                  .filter(f => selectedFeatures.has(f._index))
                  .slice(0, 5)
                  .map((feature, idx) => (
                    <tr key={idx}>
                      <td className="px-4 py-2 text-sm text-gray-900">
                        {columnMapping.alias ? feature.properties?.[columnMapping.alias] || '-' : '-'}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-900">
                        {columnMapping.section ? feature.properties?.[columnMapping.section] || '-' : '-'}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Import Mode */}
      <div className="mt-8 pt-6 border-t">
        <h3 className="font-medium text-gray-900 mb-3">Import Mode</h3>
        <div className="space-y-3">
          <label className="flex items-start gap-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="importMode"
              value="upsert"
              checked={importMode === 'upsert'}
              onChange={(e) => setImportMode(e.target.value)}
              className="mt-1 h-4 w-4 text-brand-green-600 focus:ring-brand-green-500"
            />
            <div>
              <span className="font-medium text-gray-900">Update existing, create new</span>
              <p className="text-sm text-gray-500">
                Match by alias - update geometry for existing plots, create new plots for unmatched features.
                This is the recommended option for most imports.
              </p>
            </div>
          </label>

          <label className="flex items-start gap-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="importMode"
              value="update"
              checked={importMode === 'update'}
              onChange={(e) => setImportMode(e.target.value)}
              className="mt-1 h-4 w-4 text-brand-green-600 focus:ring-brand-green-500"
            />
            <div>
              <span className="font-medium text-gray-900">Update existing only</span>
              <p className="text-sm text-gray-500">
                Only update geometry for plots that already exist in the database.
                Features without a matching plot will be skipped.
              </p>
            </div>
          </label>

          <label className="flex items-start gap-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="importMode"
              value="create"
              checked={importMode === 'create'}
              onChange={(e) => setImportMode(e.target.value)}
              className="mt-1 h-4 w-4 text-brand-green-600 focus:ring-brand-green-500"
            />
            <div>
              <span className="font-medium text-gray-900">Create new only</span>
              <p className="text-sm text-gray-500">
                Create new plots for all selected features, without checking for existing matches.
                Use this when importing data for a new section.
              </p>
            </div>
          </label>
        </div>
      </div>
    </div>
  );

  // Step 4: Match & Preview
  const renderMatchPreviewStep = () => (
    <div>
      <h2 className="text-xl font-semibold mb-2">Match Preview</h2>
      <p className="text-gray-600 mb-6">
        Review how imported features will match with existing plots in your database.
      </p>

      {isLoading ? (
        <div className="text-center py-12">
          <svg className="animate-spin h-10 w-10 mx-auto text-brand-green-600" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <p className="mt-4 text-gray-500">Analyzing matches...</p>
        </div>
      ) : importMode === 'create' ? (
        <div className="bg-blue-50 rounded-lg p-6 text-center">
          <p className="text-blue-800">
            <strong>Create new only</strong> mode selected.
            All {selectedFeatures.size} selected features will be imported as new plots.
          </p>
        </div>
      ) : matchPreview ? (
        <>
          {/* Summary */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-600">{matchPreview.summary.matched}</div>
              <div className="text-sm text-green-800">Will Update</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">{matchPreview.summary.new}</div>
              <div className="text-sm text-blue-800">Will Create</div>
            </div>
            <div className="bg-yellow-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-yellow-600">{matchPreview.summary.noAlias}</div>
              <div className="text-sm text-yellow-800">No Alias</div>
            </div>
          </div>

          {importMode === 'update' && matchPreview.summary.new > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex gap-3">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 flex-shrink-0" />
                <p className="text-sm text-yellow-800">
                  <strong>{matchPreview.summary.new}</strong> features do not match existing plots and will be skipped
                  in "Update existing only" mode. Switch to "Update existing, create new" mode to import them as new plots.
                </p>
              </div>
            </div>
          )}

          {/* Match details */}
          <div className="border rounded-lg overflow-hidden max-h-80 overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">File Alias</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Existing Plot</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {matchPreview.matches.map((match, idx) => (
                  <tr key={idx}>
                    <td className="px-4 py-2">
                      <span className={`inline-flex px-2 py-1 text-xs rounded ${
                        match.status === 'matched'
                          ? 'bg-green-100 text-green-800'
                          : match.status === 'new'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {match.status === 'matched' ? 'Update' : match.status === 'new' ? 'Create' : 'Skip'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-900">
                      {match.feature.properties?.[columnMapping.alias] || '(no alias)'}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-500">
                      {match.match ? `${match.match.alias} (ID: ${match.match.fid})` : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="text-center py-8 text-gray-500">
          No match preview available
        </div>
      )}
    </div>
  );

  // Step 5: Import
  const renderImportStep = () => (
    <div className="text-center py-8">
      {isLoading ? (
        <>
          <svg className="animate-spin h-16 w-16 mx-auto text-brand-green-600" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <h2 className="text-xl font-semibold mt-6">Importing...</h2>
          <p className="text-gray-500 mt-2">Please wait while we import your plots.</p>
        </>
      ) : importResult ? (
        <>
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-green-100 mb-6">
            <CheckCircleIcon className="h-12 w-12 text-green-600" />
          </div>

          <h2 className="text-xl font-semibold mb-2">Import Complete</h2>
          <p className="text-gray-600 mb-6">{importResult.message}</p>

          <div className="grid grid-cols-3 gap-4 max-w-md mx-auto mb-8">
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-600">{importResult.stats.created}</div>
              <div className="text-sm text-green-800">Created</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">{importResult.stats.updated}</div>
              <div className="text-sm text-blue-800">Updated</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-gray-600">{importResult.stats.skipped}</div>
              <div className="text-sm text-gray-800">Skipped</div>
            </div>
          </div>

          {importResult.stats.errors && importResult.stats.errors.length > 0 && (
            <div className="mt-6 bg-red-50 rounded-lg p-4 text-left max-w-lg mx-auto">
              <h3 className="font-medium text-red-800 mb-2">Errors ({importResult.stats.errors.length})</h3>
              <ul className="text-sm text-red-700 space-y-1 max-h-40 overflow-y-auto">
                {importResult.stats.errors.slice(0, 10).map((err, idx) => (
                  <li key={idx}>Feature #{err.index + 1}: {err.error}</li>
                ))}
                {importResult.stats.errors.length > 10 && (
                  <li className="text-red-500">... and {importResult.stats.errors.length - 10} more errors</li>
                )}
              </ul>
            </div>
          )}

          <div className="flex gap-4 justify-center mt-8">
            <button
              onClick={resetWizard}
              className="px-6 py-2 bg-brand-green-600 text-white rounded-lg hover:bg-brand-green-700"
            >
              Import Another File
            </button>
            <a
              href="/plots"
              className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              View Map
            </a>
          </div>
        </>
      ) : (
        <div className="text-center py-8 text-gray-500">
          Something went wrong. Please try again.
        </div>
      )}
    </div>
  );

  // Render current step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderUploadStep();
      case 2:
        return renderSelectStep();
      case 3:
        return renderMapColumnsStep();
      case 4:
        return renderMatchPreviewStep();
      case 5:
        return renderImportStep();
      default:
        return null;
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Import Geospatial Data</h1>
        <p className="text-gray-600">Import plot geometry from geospatial files into your cemetery map.</p>
      </div>

      {/* Step indicator */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        {renderStepIndicator()}
      </div>

      {/* Main content */}
      <div className="bg-white rounded-lg shadow p-6">
        {/* Error display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <XCircleIcon className="h-5 w-5 text-red-500 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-400 hover:text-red-600"
            >
              <XCircleIcon className="h-5 w-5" />
            </button>
          </div>
        )}

        {/* Step content */}
        {renderStepContent()}

        {/* Navigation */}
        {currentStep < 5 && (
          <div className="flex justify-between mt-8 pt-6 border-t">
            <button
              onClick={goBack}
              disabled={currentStep === 1}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Back
            </button>

            <button
              onClick={goNext}
              disabled={!canGoNext() || isLoading}
              className="flex items-center gap-2 px-6 py-2 bg-brand-green-600 text-white rounded-lg hover:bg-brand-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {currentStep === 4 ? (
                <>
                  Import {selectedFeatures.size} Features
                  <CloudArrowUpIcon className="h-4 w-4" />
                </>
              ) : (
                <>
                  Next
                  <ArrowRightIcon className="h-4 w-4" />
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GeospatialImport;
